import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Search, CheckCircle2, Circle, Lock, LogOut, Loader2, FileText, FileX } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { getAllProducts, deleteProduct, getLeads, getContacts } from "@/lib/api";
import EditProductDialog from "@/components/EditProductDialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const DatasheetBadge = ({ p, lang, t }) => {
  if (!p.source_file) {
    return (
      <span data-testid={`datasheet-missing-${p.id}`} className="inline-flex shrink-0 items-center gap-1 border border-orange-500/50 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-orange-500">
        <FileX className="h-3 w-3" /> {t("datasheetMissing")}
      </span>
    );
  }
  const d = p.datasheet_uploaded_at ? new Date(p.datasheet_uploaded_at) : null;
  const date = d && !isNaN(d) ? d.toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <span data-testid={`datasheet-present-${p.id}`} className="inline-flex shrink-0 items-center gap-1 border border-success/50 bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold text-success">
      <FileText className="h-3 w-3" /> PDF{date && ` • ${date}`}
    </span>
  );
};

export default function ManageProducts() {
  const { ready } = useAuth();
  if (!ready) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  return <ManageInner />;
}

function LoginGate({ t, login }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(t("loginError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md" data-testid="login-gate">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center border border-border bg-surface">
          <Lock className="h-6 w-6 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-black tracking-tight">{t("loginTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("loginRequired")}</p>
      </div>
      <form onSubmit={submit} className="space-y-3 border border-border bg-card p-5">
        <label className="flex flex-col gap-1.5">
          <span className="label-caps text-muted-foreground">{t("email")}</span>
          <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="border border-input bg-surface px-3 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-primary" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="label-caps text-muted-foreground">{t("password")}</span>
          <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="border border-input bg-surface px-3 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-primary" />
        </label>
        {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
        <Button data-testid="login-submit" type="submit" disabled={loading}
          className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("loginBtn")}
        </Button>
      </form>
    </div>
  );
}

function ManageInner() {
  const { t, tc, lang } = useApp();
  const { user, logout, login } = useAuth();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("__all");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [tab, setTab] = useState("products");

  const load = () => getAllProducts().then(setProducts).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  // Safety net: never let the delete AlertDialog leave body pointer-events stuck (black frozen screen)
  useEffect(() => {
    if (deleting) return;
    const clear = () => { if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = ""; };
    clear();
    const id = setTimeout(clear, 400);
    return () => clearTimeout(id);
  }, [deleting]);

  const brands = useMemo(
    () => Array.from(new Set(products.map((p) => p.brand))).sort(),
    [products]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (brand !== "__all" && p.brand !== brand) return false;
      if (!q) return true;
      return (p.name + " " + p.brand).toLowerCase().includes(q);
    });
  }, [products, query, brand]);

  const confirmDelete = async () => {
    const id = deleting?.id;
    try {
      await deleteProduct(id);
      toast.success(t("del") + " ✓");
      setDeleting(null);                                   // close the dialog first
      setTimeout(() => setProducts((prev) => prev.filter((p) => p.id !== id)), 300);
    } catch (e) {
      toast.error("Error");
    }
  };

  return (
    <div data-testid="manage-products">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">{t("manageTitle")}</h1>
          <p className="mt-2 text-base text-muted-foreground">{t("manageHint")}</p>
        </div>
        {user && <div className="shrink-0 text-right">
          <div className="label-caps text-muted-foreground">{t("loggedInAs")}</div>
          <div className="mb-1 font-mono text-xs">{user?.email}</div>
          <button data-testid="logout-btn" onClick={logout}
            className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-accent">
            <LogOut className="h-3.5 w-3.5" /> {t("logout")}
          </button>
        </div>}
      </div>

      <div className="mb-4 flex gap-2 border-b border-border">
        <button data-testid="tab-products" onClick={() => setTab("products")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${tab === "products" ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}>
          {t("viewProducts")}
        </button>
        <button data-testid="tab-leads" onClick={() => setTab("leads")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${tab === "leads" ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}>
          {t("viewLeads")}
        </button>
        <button data-testid="tab-contacts" onClick={() => setTab("contacts")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${tab === "contacts" ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}>
          {t("viewContacts")}
        </button>
      </div>

      {tab !== "products" && !user ? (
        <LoginGate t={t} login={login} />
      ) : tab === "leads" ? (
        <LeadsPanel t={t} />
      ) : tab === "contacts" ? (
        <ContactsPanel t={t} />
      ) : (
      <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center border border-input bg-surface px-3 sm:w-80">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            data-testid="manage-search"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search")}
            className="w-full bg-transparent px-2 py-2 text-sm outline-none"
          />
        </div>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger data-testid="manage-brand-filter" className="w-full rounded-none border-input bg-surface font-mono text-sm sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-72">
            <SelectItem value="__all" className="font-mono text-sm">{t("allBrands")}</SelectItem>
            {brands.map((b) => (
              <SelectItem key={b} value={b} className="font-mono text-sm">{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="font-mono text-sm text-muted-foreground sm:ml-auto">
          {filtered.length} {t("productsCount")}
        </span>
      </div>

      <div className="border border-border bg-card">
        <div className="hidden grid-cols-[2fr,1.2fr,1fr,auto] gap-2 border-b border-border bg-surface px-4 py-2.5 label-caps text-muted-foreground sm:grid">
          <span>{t("product")}</span>
          <span>{t("brand")}</span>
          <span>{t("subcategory")}</span>
          <span className="w-20 text-right">—</span>
        </div>
        {filtered.length === 0 && (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">{t("noResults")}</div>
        )}
        {filtered.map((p) => (
          <div
            key={p.id}
            data-testid={`product-row-${p.id}`}
            className="grid grid-cols-1 items-center gap-1 border-b border-border/50 px-4 py-3 sm:grid-cols-[2fr,1.2fr,1fr,auto] sm:gap-2"
          >
            <div className="flex flex-wrap items-center gap-2">
              {p.complete
                ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" title={t("completeBadge")} />
                : <Circle className="h-4 w-4 shrink-0 text-muted-foreground/50" title={t("incompleteBadge")} />}
              <span className="font-medium">{p.name}</span>
              <DatasheetBadge p={p} lang={lang} t={t} />
            </div>
            <span className="font-mono text-sm text-muted-foreground">{p.brand}</span>
            <span className="font-mono text-xs text-muted-foreground">{tc(p.subcategory)}</span>
            <div className="mt-2 flex justify-start gap-1 sm:mt-0 sm:justify-end">
              <button
                data-testid={`edit-btn-${p.id}`}
                onClick={() => setEditing(p)}
                className="flex items-center gap-1 border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-accent"
              >
                <Pencil className="h-3.5 w-3.5" /> {t("edit")}
              </button>
              <button
                data-testid={`delete-btn-${p.id}`}
                onClick={() => setDeleting(p)}
                className="flex items-center gap-1 border border-border px-2.5 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" /> {t("del")}
              </button>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      <EditProductDialog
        product={editing} open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={(u) => u && setProducts((prev) => prev.map((p) => (p.id === u.id ? { ...p, ...u } : p)))}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-none border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && <span className="font-mono">{deleting.brand} · {deleting.name}</span>}
              <br />{t("deleteConfirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={confirmDelete}
              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("del")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function ContactsPanel({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getContacts().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  return (
    <div data-testid="contacts-panel">
      <h2 className="mb-3 font-display text-xl font-bold">{t("contactsTitle")} · {items.length}</h2>
      {items.length === 0 ? (
        <div className="border border-dashed border-border px-4 py-16 text-center text-sm text-muted-foreground">{t("contactsEmpty")}</div>
      ) : (
        <div className="space-y-3">
          {items.map((c, i) => (
            <div key={c.id || i} data-testid={`contact-row-${i}`} className="border border-border bg-card p-4">
              <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-display font-bold">{c.name || "—"}</span>
                {c.company && <span className="border border-border px-2 py-0.5 text-xs text-muted-foreground">{c.company}</span>}
                <a href={`mailto:${c.email}`} className="font-mono text-xs text-primary hover:underline">{c.email}</a>
                <span className="ml-auto font-mono text-xs text-muted-foreground">{fmtDate(c.created_at)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function LeadsPanel({ t }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getLeads().then(setLeads).catch(() => setLeads([])).finally(() => setLoading(false));
  }, []);
  const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };
  const money = (v) => (typeof v === "number" ? `CHF ${v.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  return (
    <div data-testid="leads-panel">
      <h2 className="mb-3 font-display text-xl font-bold">{t("leadsTitle")} · {leads.length}</h2>
      {leads.length === 0 ? (
        <div className="border border-dashed border-border px-4 py-16 text-center text-sm text-muted-foreground">{t("leadsEmpty")}</div>
      ) : (
        <div className="overflow-x-auto border border-border bg-card">
          <div className="hidden min-w-[900px] grid-cols-[1.2fr,1.2fr,1.2fr,1fr,1.6fr,1fr,1fr,1.4fr] gap-2 border-b border-border bg-surface px-4 py-2.5 label-caps text-muted-foreground md:grid">
            <span>{t("thDate")}</span><span>{t("thCompany")}</span><span>{t("thProject")}</span><span>{t("thCity")}</span>
            <span>{t("product")}</span><span>{t("thQty")}</span><span>{t("clientPrice")}</span><span>{t("thEmail")}</span>
          </div>
          {leads.map((l, i) => (
            <div key={i} data-testid={`lead-row-${i}`} className="grid grid-cols-1 gap-1 border-b border-border/50 px-4 py-3 text-sm md:grid-cols-[1.2fr,1.2fr,1.2fr,1fr,1.6fr,1fr,1fr,1.4fr] md:gap-2">
              <span className="font-mono text-xs text-muted-foreground">{fmtDate(l.created_at)}</span>
              <span className="font-medium">{l.company || "—"}</span>
              <span>{l.project_name || "—"}</span>
              <span className="text-muted-foreground">{l.city_zip || l.city || "—"}</span>
              <span className="font-mono text-xs">{[l.brand, l.product_name].filter(Boolean).join(" ") || "—"}</span>
              <span className="font-mono text-xs">{l.qty_label || (l.area_m2 != null ? `${l.area_m2} ${l.unit || "m²"}` : "—")}</span>
              <span className="font-mono font-semibold">{money(l.client_price)}</span>
              <span className="truncate font-mono text-xs text-muted-foreground">{l.recipient_email || "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
